import React, { useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import FadeIn from 'react-fade-in';
import { useObserver } from 'mobx-react-lite';
import { GlobalHotKeys } from 'react-hotkeys';
import { toast } from 'react-toastify';

import BodyEnd from '../../_shared/BodyEnd';
import ConfirmModal from '../../_shared/ConfirmModalComponent';
import QuickDrawerHeader from '../../_shared/QuickDrawerHeader';
import { quickDrawerFocus, renderQuickDrawerLoading } from '../../_shared/QuickDrawer';

import PredefinedExamViewStore from '../../../../stores/PredefinedExamViewStore';
import QuickDrawerStore from '../../../../stores/QuickDrawerStore';

import * as AccessType from '../../../../constants/accessTypes';
import * as rts from '../../../../constants/routes';
import * as fn from '../../../../utilities/_functions';
import * as oh from '../../../../utilities/operationHelper';
import * as ach from '../../../../utilities/accessHelper';

import './ManagePredefinedExamTemplates.scss';

function ManagePredefinedExamTemplates(props) {
    const isMounted = useRef(true);
    const validateRef = useRef(null);
    const confirmModalRef = useRef(null);
    const focusTimer = useRef(null);
    const predefinedExam = useContext(PredefinedExamViewStore);
    const quickDrawer = useContext(QuickDrawerStore);
    const navigate = useNavigate();

    useEffect(() => {
        quickDrawerFocus(props.drawer);

        return () => {
            isMounted.current = false;
            if (focusTimer.current) { clearTimeout(focusTimer.current); focusTimer.current = null; }
        }
    }, []) // eslint-disable-line

    const handleExamTemplateChange = event => {
        const { value } = event.target;
        predefinedExam.selectedExamTemplateId = value ? value : null;
    }

    const handlePredefinedTemplateNew = event => {
        predefinedExam.clearNewTemplate();

        quickDrawer.activateQuickDrawer('exam', 'predefined-template-create', null, handlePredefinedTemplateNewSuccess, handlePredefinedTemplateNewCancel)
            .then(drawer => {
                if (isMounted.current) {
                    focusTimer.current = setTimeout(() => {
                        quickDrawerFocus(drawer);
                    }, 100);
                }
            });
    }

    const handlePredefinedTemplateNewSuccess = (result) => {
        if (result && result.data && result.data.id) {
            navigate(`${rts.Exams.PrefillTemplate}/${result.data.id}`);
            toast.dark(() => <p>Exam Prefill Template added.</p>);
        }
    }

    const handlePredefinedTemplateNewCancel = event => {
        predefinedExam.clearNewTemplate();
    }

    const handlePredefinedTemplateDelete = event => {
        confirmModalRef.current.show({
            icon: <i className='fal fa-times-square text-danger mr-2'></i>,
            message: <>Continue to <span className='fw-500'>&nbsp;delete the [{predefinedExam.selectedPredefinedTemplate ? predefinedExam.selectedPredefinedTemplate.name : ''}] prefill exam template</span>? </>,
            onOption1Click: handlePredefinedTemplateDeleteConfirm,
            onCancel: handlePredefinedTemplateDeleteCancel,
        })
    }

    const handlePredefinedTemplateDeleteConfirm = event => {
        predefinedExam.delete()
            .then(() => {
                predefinedExam.refresh();
                toast.dark(() => <p>Prefill exam template deleted.</p>);
                confirmModalRef.current.close();
            });
    }

    const handlePredefinedTemplateDeleteCancel = event => {
        confirmModalRef.current.close();
    }

    const handleSubmit = event => {
        event.preventDefault();

        if (fn.validateForm(validateRef.current)) {
            if (isMounted.current) {
                navigate(`${rts.Exams.PrefillTemplate}/${predefinedExam.selectedPredefinedTemplateId}`);
                quickDrawer.deactivateAll();
            }
        }
    }

    return useObserver(() => <>
        <>
            {
                (props.drawer === quickDrawer.drawerOpened) ?
                    <GlobalHotKeys
                        keyMap={{
                            close: ['esc'],
                        }}
                        handlers={{
                            close: event => {
                                props.onCancel(event)
                            },
                        }}
                        allowChanges={true}
                    /> : null
            }
        </>
        <form ref={validateRef} onSubmit={handleSubmit}>
            <fieldset disabled={predefinedExam.isSaving}>
                <div className='quick-drawer'>
                    <QuickDrawerHeader
                        drawer={props.drawer}
                        icon={oh.getIcon('exam')}
                        action='Manage'
                        category='Prefill Exam Templates'
                        className='exams'
                        onCancel={props.onCancel}
                    />
                    <div className='quick-drawer-body'>
                        {
                            predefinedExam.isReady ?
                                <FadeIn>
                                    <div className='predefined-exam-templates body-content'>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group validate validate-required mb-0'>
                                                        <label className='required' htmlFor='exam-template'><small>Exam Type</small></label>
                                                        <select
                                                            id='exam-template'
                                                            className='custom-select form-control'
                                                            value={predefinedExam.selectedExamTemplateId ? predefinedExam.selectedExamTemplateId : ''}
                                                            onChange={handleExamTemplateChange}
                                                        >
                                                            <option value=''></option>
                                                            {
                                                                predefinedExam.examTemplates && predefinedExam.examTemplates.length > 0 ?
                                                                    predefinedExam.examTemplates.map((t, ti) => {
                                                                        return <option
                                                                            key={`exam_template_${ti}`}
                                                                            value={t.id}
                                                                        >{t.name}</option>
                                                                    }) :
                                                                    <option value=''></option>
                                                            }
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        <section>
                                            <div className='row'>
                                                <div className='col-12'>
                                                    <div className='form-group mb-0'>
                                                        <label className='required'><small>Your templates <span className='tt-normal'>(Only you can access them)</span></small></label>
                                                        {
                                                            predefinedExam.selectedUserPredefinedTemplates && predefinedExam.selectedUserPredefinedTemplates.length > 0 ?
                                                                <>
                                                                    {
                                                                        predefinedExam.selectedUserPredefinedTemplates.map((d, di) => {
                                                                            return <div
                                                                                key={`user-predefined-exam-${di}`}
                                                                                className='custom-control custom-radio mb-2'
                                                                            >
                                                                                <input
                                                                                    id={`user-predefined-exam-${di}`}
                                                                                    type='radio'
                                                                                    name='predefinedExam'
                                                                                    className='custom-control-input'
                                                                                    value={d.id}
                                                                                    checked={predefinedExam.selectedPredefinedTemplateId === d.id}
                                                                                    onChange={() => { predefinedExam.selectedPredefinedTemplateId = d.id }}
                                                                                />
                                                                                <label
                                                                                    htmlFor={`user-predefined-exam-${di}`}
                                                                                    className='custom-control-label'
                                                                                >
                                                                                    {d.name}
                                                                                </label>
                                                                            </div>
                                                                        })
                                                                    }
                                                                </> : <div>
                                                                    <button
                                                                        type='button'
                                                                        className='btn btn-link p-0'
                                                                        onClick={handlePredefinedTemplateNew}
                                                                    >
                                                                        Create your first prefill template
                                                                    </button>
                                                                </div>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        </section>
                                        {
                                            ach.check(AccessType.UPDATE_EXAM_PREDEFINED_DATA_SHARED) ?
                                                <section>
                                                    <div className='row'>
                                                        <div className='col-12'>
                                                            <div className='form-group mb-0'>
                                                                <label className='required'><small>Shared templates <span className='tt-normal'>(Everyone can access them)</span></small></label>
                                                                {
                                                                    predefinedExam.selectedPublicPredefinedTemplates && predefinedExam.selectedPublicPredefinedTemplates.length > 0 ?
                                                                        <>
                                                                            {
                                                                                predefinedExam.selectedPublicPredefinedTemplates.map((d, di) => {
                                                                                    return <div
                                                                                        key={`public-predefined-exam-${di}`}
                                                                                        className='custom-control custom-radio mb-2'
                                                                                    >
                                                                                        <input
                                                                                            id={`public-predefined-exam-${di}`}
                                                                                            type='radio'
                                                                                            name='predefinedExam'
                                                                                            className='custom-control-input'
                                                                                            value={d.id}
                                                                                            checked={predefinedExam.selectedPredefinedTemplateId === d.id}
                                                                                            onChange={() => { predefinedExam.selectedPredefinedTemplateId = d.id }}
                                                                                        />
                                                                                        <label
                                                                                            htmlFor={`public-predefined-exam-${di}`}
                                                                                            className='custom-control-label'
                                                                                        >
                                                                                            {d.name}
                                                                                        </label>
                                                                                    </div>
                                                                                })
                                                                            }
                                                                        </> : <em className='text-gray-600'>None</em>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </section> : null
                                        }
                                    </div>
                                </FadeIn> : renderQuickDrawerLoading()
                        }
                    </div>
                    <div className='quick-drawer-action'>
                        <div className='row'>
                            <div className='col-4'>
                                <button
                                    type='button'
                                    className='btn btn-light'
                                    onClick={handlePredefinedTemplateNew}
                                >Add</button>
                            </div>
                            <div className='col-8'>
                                <div className='float-right'>
                                    <button
                                        type='button'
                                        className='btn btn-danger mr-2'
                                        disabled={!predefinedExam.selectedPredefinedTemplate}
                                        onClick={handlePredefinedTemplateDelete}
                                    >Delete</button>
                                    <button
                                        type='submit'
                                        className='btn btn-primary'
                                        disabled={!predefinedExam.selectedPredefinedTemplate}
                                    >Update</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </fieldset>
        </form>
        <BodyEnd>
            <ConfirmModal ref={confirmModalRef} />
        </BodyEnd>
    </>)
}

export default ManagePredefinedExamTemplates;